import React, { useCallback, useState } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { Trans } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Divider, FilterActions, Popover, Stack, Typography } from '@lyearn/ui';
import NumberInput from '@/components/NumberInput';
import TagDatePicker, {
  InlineTagDatePicker,
} from '@/modules/corporate/modules/tags/components/TagDatePicker';
import { DateValue, TagValues } from '@/modules/corporate/modules/tags/types';
import { FilterOperatorEnum } from '@/types/schema';

import { dynamicTimeWidth, numberInputStyles } from '../styles';
import { DateValueTypeEnumSM, OnFilterChange, RelativeDateFilterOperatorEnumSM } from '../types';
import { getOffsetRelationi18Text, INITIAL_DATE_FILTER_STATE } from '../utils';
import DateValueTypeSelector from './DateValueTypeSelector';
import OffsetRelationSelector from './OffsetRelationSelector';

type DateConditionSelectorProps = {
  includeEndDate: boolean;
  includeTime: boolean;
  dateTagValue?: DateValue | null;
  onChange: (val: OnFilterChange) => void;
  children: React.ReactNode;
  operator?: FilterOperatorEnum;
  dateValueType: DateValueTypeEnumSM;
  offsetRelation: RelativeDateFilterOperatorEnumSM;
  offsetDays: number;
  disableValueTypeSelector?: boolean;
};

function DateConditionSelector(props: DateConditionSelectorProps) {
  const { t } = useTranslation();
  const [open, { toggle }] = useToggle(false);
  const {
    includeEndDate,
    includeTime,
    children,
    dateTagValue,
    onChange,
    operator,
    disableValueTypeSelector,
  } = props;
  const [dateFilterState, setDateFilterState] = useState<OnFilterChange>({
    tagValues: dateTagValue,
    offsetRelation: props.offsetRelation,
    dateValueType: props.dateValueType,
    offsetDays: props.offsetDays,
  });

  const { tagValues, offsetRelation, dateValueType, offsetDays } = dateFilterState;

  const setTagValues = useCallback(
    (val: TagValues) =>
      setDateFilterState((prevState) => ({
        ...prevState,
        tagValues: val,
      })),
    [],
  );

  const setOffsetRelation = useCallback(
    (offsetRelation: RelativeDateFilterOperatorEnumSM) =>
      setDateFilterState((prevState) => ({
        ...prevState,
        offsetRelation,
      })),
    [],
  );

  const setDateValueType = useCallback(
    (dateValueType: DateValueTypeEnumSM) =>
      setDateFilterState((prevState) => ({
        ...prevState,
        dateValueType,
      })),
    [],
  );

  const setOffsetDays = useCallback(
    (offsetDays: number) =>
      setDateFilterState((prevState) => ({
        ...prevState,
        offsetDays,
      })),
    [],
  );

  const onFilterSave = useCallback(() => {
    onChange(dateFilterState);
    toggle();
  }, [dateFilterState, onChange, toggle]);

  const onBetweenOperatorFilterSave = useCallback(
    (val: TagValues) => {
      onChange({
        tagValues: val,
      });
    },
    [onChange],
  );

  const onClear = useCallback(
    () =>
      setDateFilterState((prevState) => ({
        ...INITIAL_DATE_FILTER_STATE,
        dateValueType: prevState.dateValueType,
      })),
    [],
  );

  if (operator === FilterOperatorEnum.Between) {
    return (
      <TagDatePicker
        includeEndDate={includeEndDate}
        includeTime={includeTime}
        selectedTagValue={props.dateTagValue}
        onChange={onBetweenOperatorFilterSave}>
        {children}
      </TagDatePicker>
    );
  }

  return (
    <Popover open={open} onOpenChange={toggle}>
      <Popover.Button>{children}</Popover.Button>
      <Popover.Content>
        <Stack className="pb-8" direction="col">
          <Stack className="py-12 px-20">
            <DateValueTypeSelector
              dateValueType={dateValueType}
              disabled={disableValueTypeSelector}
              onChange={setDateValueType}
            />
          </Stack>
          <Divider />
          {dateValueType === DateValueTypeEnumSM.SpecificDate ? (
            <InlineTagDatePicker
              includeEndDate={includeEndDate}
              includeTime={includeTime}
              selectedTagValue={tagValues}
              onChange={setTagValues}
            />
          ) : (
            <Stack className={cn('py-12 px-20', dynamicTimeWidth)} direction="col" gapY={16}>
              <Stack className="w-full">
                <NumberInput
                  canBeEmpty
                  className={cn(numberInputStyles, 'w-1/2 me-8')}
                  inputProps={{ style: { width: `${offsetDays?.toString().length}.1ch` } }}
                  inputSuffix={t('Common:Reminder.Units.Days')}
                  min={0}
                  placeholder={t('Common:Reminder.Units.Days')}
                  size="medium"
                  value={offsetDays || 0}
                  onChange={setOffsetDays}
                />
                <OffsetRelationSelector
                  offsetRelation={offsetRelation}
                  onChange={setOffsetRelation}
                />
              </Stack>
              <Typography className="px-8 pb-4" color="text-text-secondary" variant="body-short-02">
                <Trans
                  i18nKey={'Group:Editor.DynamicDateDescription'}
                  values={{
                    dimension: getOffsetRelationi18Text(t, offsetRelation),
                    days: offsetDays,
                    count: offsetDays,
                  }}
                />
              </Typography>
            </Stack>
          )}
          <FilterActions onClear={onClear} onSave={onFilterSave} />
        </Stack>
      </Popover.Content>
    </Popover>
  );
}

export default DateConditionSelector;
